import { useCallback, useMemo, useState } from "react";
import { showSuccessToast } from "../../../components/toast/toastBus";
import { uploadFile, deleteFileByUrl } from "../../../services/fileService";
import { deleteArmaById, savePersona } from "../api/perfilesApi";
import { validatePerfil } from "../domain/perfilValidation";
import { buildPerfilPayload } from "../domain/perfilPayload";
import { isBlank } from "../domain/perfilForm";

export function usePerfilSave({
    form,
    setForm,
    personaId,
    onPersonaCreada,
    tiposDocumento,
    tipoArmas,
    tipoLicencias,
    files, // { fileDocIdentidad, fileLicenciaCaza, fileSeguro, fileLicenciaArmas, fileFoto }
    setFiles, // setters agrupados
    armasGuiaFiles,
    setArmasGuiaFiles,
}) {
    const [errores, setErrores] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const clearFieldOnly = useCallback(
        (field) => {
            setForm((prev) => ({ ...prev, [field]: "" }));

            if (field === "foto") {
                setFiles((prev) => ({ ...prev, fileFoto: null }));
            }
            if (field === "imagenDocumento") setFiles((prev) => ({ ...prev, fileDocIdentidad: null }));
            if (field === "licenciaCaza_rutaDocumento") setFiles((prev) => ({ ...prev, fileLicenciaCaza: null }));
            if (field === "seguro_rutaDocumento") setFiles((prev) => ({ ...prev, fileSeguro: null }));
            if (field === "licenciaArmas_rutaDocumento") setFiles((prev) => ({ ...prev, fileLicenciaArmas: null }));
        },
        [setForm, setFiles]
    );

    const deleteFromServerAndClear = useCallback(
        async (field, url) => {
            if (!url) return clearFieldOnly(field);
            if (!window.confirm("Esto borrará el archivo del servidor. ¿Continuar?")) return;

            try {
                await deleteFileByUrl(url);
                clearFieldOnly(field);
                showSuccessToast("Archivo borrado del servidor");
            } catch (e) {
                console.error(e);
                alert("No se pudo borrar el archivo en el servidor");
            }
        },
        [clearFieldOnly]
    );

    const handleEliminarArmaSafe = useCallback(
        async (index, armaId) => {
            if (!armaId) {
                setForm((prev) => {
                    const copy = [...prev.armas];
                    copy.splice(index, 1);
                    return { ...prev, armas: copy };
                });
                return;
            }

            if (!window.confirm("¿Estás seguro de que quieres eliminar esta arma?")) return;

            try {
                await deleteArmaById(armaId);

                setForm((prev) => {
                    const copy = [...prev.armas];
                    copy.splice(index, 1);
                    return { ...prev, armas: copy };
                });

                showSuccessToast("Arma eliminada correctamente");
            } catch (error) {
                console.error("Error al eliminar arma:", error);
                const friendly =
                    typeof error?.message === "string" && error.message.includes("referenciada")
                        ? "Esta arma está referenciada en otra entidad y no puede eliminarse."
                        : "No se pudo eliminar el arma. Intenta de nuevo.";
                alert(friendly);
            }
        },
        [setForm]
    );

    const validate = useCallback(() => {
        const next = validatePerfil(form);
        setErrores(next);
        return Object.keys(next).length === 0;
    }, [form]);

    const handleSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            if (!validate()) return;
            if (isSubmitting) return;

            setIsSubmitting(true);

            try {
                const {
                    fileDocIdentidad,
                    fileLicenciaCaza,
                    fileSeguro,
                    fileLicenciaArmas,
                    fileFoto,
                } = files;

                const [docIdRes, licCazaRes, seguroRes, licArmasRes, fotoRes] = await Promise.all([
                    fileDocIdentidad ? uploadFile(fileDocIdentidad, "personas/documentos") : Promise.resolve(null),
                    fileLicenciaCaza ? uploadFile(fileLicenciaCaza, "personas/licencia-caza") : Promise.resolve(null),
                    fileSeguro ? uploadFile(fileSeguro, "personas/seguros") : Promise.resolve(null),
                    fileLicenciaArmas ? uploadFile(fileLicenciaArmas, "personas/licencias-armas") : Promise.resolve(null),
                    fileFoto ? uploadFile(fileFoto, "personas/fotos") : Promise.resolve(null),
                ]);

                const guiaUploads = await Promise.all(
                    (form.armas || []).map((_, index) =>
                        armasGuiaFiles[index] ? uploadFile(armasGuiaFiles[index], "armas/guias") : Promise.resolve(null)
                    )
                );

                const uploadedUrls = {
                    imagenDocumento: docIdRes?.url ?? form.imagenDocumento ?? null,
                    licenciaCaza_rutaDocumento: licCazaRes?.url ?? form.licenciaCaza_rutaDocumento ?? null,
                    seguro_rutaDocumento: seguroRes?.url ?? form.seguro_rutaDocumento ?? null,
                    licenciaArmas_rutaDocumento: licArmasRes?.url ?? form.licenciaArmas_rutaDocumento ?? null,
                    foto: fotoRes?.url ?? form.foto ?? null,
                };

                const payload = buildPerfilPayload({
                    form,
                    tiposDocumento,
                    tipoArmas,
                    tipoLicencias,
                    uploadedUrls,
                    guiaUploads,
                });

                const personaActualizada = await savePersona({ personaId, payload });
                onPersonaCreada(personaActualizada);
                showSuccessToast(personaId ? "Persona actualizada con éxito" : "Persona creada con éxito");
            } catch (error) {
                console.error("Error al guardar persona:", error);
                alert("Error inesperado al guardar persona");
            } finally {
                setIsSubmitting(false);
            }
        },
        [
            validate,
            isSubmitting,
            files,
            form,
            armasGuiaFiles,
            personaId,
            onPersonaCreada,
            tiposDocumento,
            tipoArmas,
            tipoLicencias,
        ]
    );

    const helpers = useMemo(
        () => ({
            errores,
            setErrores,
            isSubmitting,
            handleSubmit,
            clearFieldOnly,
            deleteFromServerAndClear,
            handleEliminarArmaSafe,
            setArmasGuiaFiles,
        }),
        [
            errores,
            isSubmitting,
            handleSubmit,
            clearFieldOnly,
            deleteFromServerAndClear,
            handleEliminarArmaSafe,
            setArmasGuiaFiles,
        ]
    );

    return helpers;
}
